import React, { useState } from 'react';
import { Laptop, Plus, X, HelpCircle, Search, PlusCircle } from 'lucide-react';

/**
 * Компонент раздела с расчетом по электроприборам
 */
const AppliancesSection = ({ 
  appliancesList,
  setAppliancesList,
  errors
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [customAppliance, setCustomAppliance] = useState({
    name: '',
    power: '',
    hoursPerDay: '',
    quantity: '1'
  });

  // Фильтрация списка по поисковому запросу
  const filteredAppliances = searchTerm 
    ? appliancesList.filter(app => app.name.toLowerCase().includes(searchTerm.toLowerCase()))
    : appliancesList;

  // Изменение статуса прибора (вкл/выкл)
  const toggleAppliance = (id) => {
    setAppliancesList(
      appliancesList.map(app => 
        app.id === id ? { ...app, active: !app.active } : app
      )
    );
  };

  // Изменение количества приборов
  const updateQuantity = (id, value) => {
    const quantity = parseInt(value);
    if (isNaN(quantity) || quantity < 1) return;

    setAppliancesList(
      appliancesList.map(app => 
        app.id === id ? { ...app, quantity } : app
      )
    );
  };

  // Изменение часов использования
  const updateHours = (id, value) => {
    const hours = parseFloat(value);
    if (isNaN(hours) || hours < 0 || hours > 24) return;

    setAppliancesList(
      appliancesList.map(app => 
        app.id === id ? { ...app, hoursPerDay: hours } : app
      )
    );
  };

  // Добавление пользовательского прибора
  const addCustomAppliance = () => {
    if (!customAppliance.name || !customAppliance.power || !customAppliance.hoursPerDay) {
      return;
    }

    const power = parseInt(customAppliance.power);
    const hours = parseFloat(customAppliance.hoursPerDay);
    const quantity = parseInt(customAppliance.quantity) || 1;

    if (isNaN(power) || power <= 0 || isNaN(hours) || hours <= 0 || hours > 24 || isNaN(quantity) || quantity <= 0) {
      return;
    }

    const newAppliance = {
      id: `custom_${Date.now()}`,
      name: customAppliance.name,
      power: power,
      hoursPerDay: hours,
      quantity: quantity,
      usageFactor: 1.0,
      active: true
    };

    setAppliancesList([...appliancesList, newAppliance]);
    
    // Сброс формы
    setCustomAppliance({
      name: '',
      power: '',
      hoursPerDay: '',
      quantity: '1'
    });
    setShowAddCustom(false);
  };

  // Расчет месячного потребления для отображения
  const calculateMonthlyUsage = (appliance) => {
    return ((appliance.power / 1000) * appliance.hoursPerDay * 30 * appliance.quantity * appliance.usageFactor).toFixed(1);
  };

  return (
    <div className="mb-6 bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Laptop className="w-5 h-5 mr-2 text-green-500" />
        Расчет по электроприборам
      </h3>

      {/* Поиск приборов */}
      <div className="mb-4 relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Поиск электроприборов..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="form-input w-full pl-9"
        />
      </div>

      {errors.appliances && (
        <p className="mb-3 text-sm text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded border border-red-200 dark:border-red-800">
          {errors.appliances}
        </p>
      )}

      {/* Список приборов */}
      <div className="mb-4 grid grid-cols-1 gap-3 max-h-96 overflow-y-auto pr-1">
        {filteredAppliances.map(appliance => (
          <div 
            key={appliance.id} 
            className={`p-3 rounded-lg border transition-colors ${
              appliance.active 
                ? 'bg-white dark:bg-gray-800 border-green-300 dark:border-green-700' 
                : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={appliance.active}
                  onChange={() => toggleAppliance(appliance.id)}
                  className="form-checkbox"
                />
                <span className={`ml-2 font-medium ${
                  appliance.active ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {appliance.name}
                </span>
              </label>
              <span className={`text-sm ${
                appliance.active ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'
              }`}>
                {appliance.active ? `${calculateMonthlyUsage(appliance)} кВт⋅ч/мес` : 'Выключено'}
              </span>
            </div>

            {appliance.active && (
              <div className="grid grid-cols-3 gap-2 ml-7">
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Часов в день
                  </label>
                  <input
                    type="number"
                    value={appliance.hoursPerDay}
                    onChange={(e) => updateHours(appliance.id, e.target.value)}
                    className="form-input w-full text-sm py-1"
                    min="0"
                    max="24"
                    step="0.5"
                  />
                </div>
              </div>
            )}
          </div>
        ))}
        
        {filteredAppliances.length === 0 && (
          <div className="text-center p-4 text-gray-500 dark:text-gray-400">
            По запросу "{searchTerm}" ничего не найдено
          </div>
        )}
      </div>

      {/* Кнопка добавления пользовательского прибора */}
      {!showAddCustom ? (
        <button 
          type="button"
          onClick={() => setShowAddCustom(true)}
          className="w-full flex items-center justify-center py-2 px-3 bg-white dark:bg-gray-700 border border-dashed border-green-300 dark:border-green-700 rounded-lg text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
        >
          <PlusCircle className="w-4 h-4 mr-2" />
          <span>Добавить свой электроприбор</span>
        </button>
      ) : (
        <div className="p-3 bg-white dark:bg-gray-800 border border-green-300 dark:border-green-700 rounded-lg">
          <div className="flex justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">Добавить свой прибор</h4>
            <button 
              type="button" 
              onClick={() => setShowAddCustom(false)}
              className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
            <div className="sm:col-span-2">
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Название прибора
              </label>
              <input
                type="text"
                value={customAppliance.name}
                onChange={(e) => setCustomAppliance({...customAppliance, name: e.target.value})}
                placeholder="Кухонный комбайн"
                className="form-input w-full text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Мощность (Вт)
              </label>
              <input
                type="number"
                value={customAppliance.power}
                onChange={(e) => setCustomAppliance({...customAppliance, power: e.target.value})}
                placeholder="500"
                className="form-input w-full text-sm"
                min="1"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Часов в день
              </label>
              <input
                type="number"
                value={customAppliance.hoursPerDay}
                onChange={(e) => setCustomAppliance({...customAppliance, hoursPerDay: e.target.value})}
                placeholder="1"
                className="form-input w-full text-sm"
                min="0.1"
                max="24"
                step="0.1"
              />
            </div>
          </div>
          
          <div className="flex justify-between items-center">
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Количество
              </label>
              <input
                type="number"
                value={customAppliance.quantity}
                onChange={(e) => setCustomAppliance({...customAppliance, quantity: e.target.value})}
                className="form-input w-20 text-sm"
                min="1"
                step="1"
              />
            </div>
            <button 
              type="button" 
              onClick={addCustomAppliance}
              className="py-1.5 px-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
              disabled={!customAppliance.name || !customAppliance.power || !customAppliance.hoursPerDay}
            >
              <Plus className="w-4 h-4 inline mr-1" />
              Добавить
            </button>
          </div>
        </div>
      )}

      {/* Расчет потребления */}
      <div className="mt-4 bg-white dark:bg-gray-800 p-3 rounded-lg border border-green-200 dark:border-green-800">
        <div className="flex items-start gap-2">
          <HelpCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <h4 className="font-medium text-gray-900 dark:text-white mb-1">Как рассчитывается потребление?</h4>
            <p className="text-gray-600 dark:text-gray-400 text-xs">
              Месячное потребление для прибора = <span className="font-mono">Мощность (кВт) × Часы использования × 30 дней × Количество</span>
            </p>
            <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">
              Примеры: холодильник (150 Вт) работает ~8 часов в день = 36 кВт⋅ч/мес, телевизор (120 Вт) при просмотре 4 часа в день = 14.4 кВт⋅ч/мес.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppliancesSection;